import Layout from './layout.js';
import BaseSideLayout from './baseSideLayout.js';
import CoordRadializer from './coordRadializer.js';
import CoordUnradializer from './coordUnradializer.js';
import RemapCoordMask from './remapCoordMask.js';
import LabelRepel2D from './labelRepel2D.js';
import { Side, SideParams } from './side.js';

class CircularLayout extends Layout {
    constructor({ repelStrength, repelMaxIterations }) {
        super();

        this._labelRepel = new LabelRepel2D();

        this._labels = new Set();
        this._leftLayout = new BaseSideLayout({
            side: Side.LEFT,
        });

        this._radParams = {
            rectangle: {
                width: null,
                height: null,
            },
            circle: {
                center: {
                    x: null,
                    y: null,
                },
                radius: null,
            },
        };

        this._mask = null;
        this._radializer = null;
        this._unradializer = null;
        this._radTransform = null;
        this._unradTransform = null;
        this._radMask = new RemapCoordMask(null, null, null, null);;

        this._repelStrength = repelStrength;
        this._repelMaxIterations = repelMaxIterations;
    }

    _updateRadialParams() {
        const { rectangle, circle } = this._radParams;

        if (this._mask) {
            const { width, height } = this._mask;

            rectangle.width = Math.round(height/2);
            rectangle.height = Math.round(height*Math.PI);

            circle.center.x = Math.round(width/2);
            circle.center.y = Math.round(height/2);
            circle.radius = Math.round(width/2);

            this._radializer = new CoordRadializer(this._radParams);
            this._unradializer = new CoordUnradializer(this._radParams);
            this._radTransform = this._radializer.makeTransform();
            this._unradTransform = this._unradializer.makeTransform();
        } else {
            rectangle.width = null;
            rectangle.height = null;

            circle.center.x = null;
            circle.center.y = null;
            circle.radius = null;

            this._radializer = null;
            this._unradializer = null;
            this._radTransform = null;
            this._unradTransform = null;
        }
    }

    addLabel(id) {
        this._leftLayout.addLabel(id);
        this._labels.add(id);
        this._labelRepel.addLabel(id);
    }

    removeLabel(id) {
        this._leftLayout.removeLabel(id);
        this._labels.delete(id);
        this._labelRepel.removeLabel(id);
        this._labelRepel.removeLabelBox(id);
    }

    setLabel(id, data) {
        if ('pointPosition' in data || 'labelPosition' in data) {
            let { pointPosition, labelPosition } = data;

            if (this._mask !== null || (!pointPosition && !labelPosition)) {
                if (pointPosition) {
                    let { x, y } = pointPosition;
                    [x, y] = this._radTransform(x, y);
                    pointPosition = { x, y };
                } else {
                    pointPosition = null;
                }

                if (labelPosition) {
                    let { x, y } = labelPosition;
                    [x, y] = this._radTransform(x, y);
                    labelPosition = { x, y };
                } else {
                    labelPosition = null;
                }

                this._leftLayout.setLabel(id, { pointPosition, labelPosition });
            }
        }

        if ('size' in data) {
            const { size } = data;

            if (size) {
                const halfWidth = size.width*0.5;
                const halfHeight = size.height*0.5;
                this._labelRepel.setLabelBox(id, {
                    min: { x: -halfWidth, y: -halfHeight },
                    max: { x: halfWidth, y: halfHeight },
                });
            } else {
                this._labelRepel.removeLabelBox(id);
            }
        }
    }

    setMask(mask) {
        this._mask = mask;
        this._updateRadialParams();

        if (mask) {
            const { rectangle } = this._radParams;
            this._radMask.setSize(rectangle.width, rectangle.height);
            this._radMask.setTargetMask(mask);
            this._radMask.setRemapper(this._unradializer);
            this._leftLayout.setMask(this._radMask);
        } else {
            this._leftLayout.setMask(null);
        }
    }

    getLabel(id) {
        let data = this._leftLayout.getLabel(id);

        if (data !== null) {
            let { pointPosition, labelPosition } = data;

            if (pointPosition !== null) {
                let { x, y } = pointPosition;
                [x, y] = this._unradTransform(x, y);
                pointPosition = { x, y };
            }

            if (labelPosition !== null) {
                let { x, y } = labelPosition;
                [x, y] = this._unradTransform(x, y);
                labelPosition = { x, y };
            }

            data = { pointPosition, labelPosition };
        }

        return data;
    }

    setRepel(strength, iterations) {
        this._repelStrength = strength;
        this._repelMaxIterations = iterations;
    }

    update(useMask) {
        this._leftLayout.update(useMask);

        const radTransform = this._radTransform;
        const unradTransform = this._unradTransform;

        const labelPositions = new Map();

        for (let id of this._labels) {
            const radData = this._leftLayout.getLabel(id);

            if (radData) {
                this._labelRepel.addLabel(id);

                let { x, y } = radData.labelPosition;
                [x, y] = unradTransform(x, y);
                const labelPosition = { x, y };
                labelPositions.set(id, labelPosition);

                this._labelRepel.setLabelPosition(id, labelPosition);
            } else {
                this._labelRepel.removeLabel(id);
            }
        }

        const repelStrength = this._repelStrength;
        const repelMaxIterations = this._repelMaxIterations;

        let labelsPushed = false;

        for (let i = 0; i < repelMaxIterations; i++) {
            if (!this._labelRepel.calculate()) {
                break;
            }

            for (let [id, labelPosition] of labelPositions) {
                const data = this._leftLayout.getLabel(id);

                const push = this._labelRepel.getLabelPush(id);

                labelPosition.x += push.x*repelStrength;
                labelPosition.y += push.y*repelStrength;
                let { x, y } = labelPosition;

                let [radX, radY] = radTransform(x, y);
                data.labelPosition.y = radY;

                [x, y] = unradTransform(data.labelPosition.x, data.labelPosition.y);

                this._leftLayout.setLabel(id, data);
                this._labelRepel.setLabelPosition(id, { x, y });
            }

            labelsPushed = true;
        }

        if (useMask && labelsPushed) {
            for (let [id, labelPosition] of labelPositions) {
                const data = this._leftLayout.getLabel(id);

                const { x: lx, y: ly } = labelPosition;
                let nearestX, nearestY, nearestDistSq = Infinity;

                const nearestPoints = this._leftLayout.regionLayerNearestPoints.nearestPoints.get(id);
                for (let [radY, radX] of nearestPoints.position) {
                    const [x, y] = unradTransform(radX, radY);

                    const dx = x-lx, dy = y-ly;
                    const distSq = dx*dx+dy*dy;

                    if (distSq < nearestDistSq) {
                        nearestX = radX;
                        nearestY = radY;
                        nearestDistSq = distSq;
                    }
                }

                data.pointPosition.x = nearestX;
                data.pointPosition.y = nearestY;
                this._leftLayout.setLabel(id, data);
            }
        }
    }
}

export default CircularLayout;
